import i18n from "i18next";
import HttpBackend from "i18next-http-backend";
import { initReactI18next } from "react-i18next";

import { detectLanguage, SUPPORTED_LOCALES, LOCALE_STORAGE_KEY } from "./detectLanguage";
import type { SupportedLocale } from "./detectLanguage";

// English is bundled inline (synchronous, always present as fallback)
import enCommon from "./locales/en/common.json";
import enAuth from "./locales/en/auth.json";
import enDashboard from "./locales/en/dashboard.json";
import enLiveStream from "./locales/en/liveStream.json";
import enMedia from "./locales/en/media.json";
import enBilling from "./locales/en/billing.json";
import enAdmin from "./locales/en/admin.json";
import enErrors from "./locales/en/errors.json";
import enValidation from "./locales/en/validation.json";
import enOverlayEditor from "./locales/en/overlayEditor.json";

export { SUPPORTED_LOCALES, LOCALE_STORAGE_KEY };
export type { SupportedLocale };

export const LOCALE_LABELS: Record<SupportedLocale, string> = {
  en: "English",
  es: "Español",
  "pt-BR": "Português (Brasil)",
  fr: "Français",
  de: "Deutsch",
  th: "ไทย",
};

export const NAMESPACES = [
  "common",
  "auth",
  "dashboard",
  "liveStream",
  "media",
  "billing",
  "admin",
  "errors",
  "validation",
  "overlayEditor",
] as const;

const englishResources = {
  common: enCommon,
  auth: enAuth,
  dashboard: enDashboard,
  liveStream: enLiveStream,
  media: enMedia,
  billing: enBilling,
  admin: enAdmin,
  errors: enErrors,
  validation: enValidation,
  overlayEditor: enOverlayEditor,
};

void i18n
  .use(HttpBackend)
  .use(initReactI18next)
  .init({
    lng: detectLanguage(),
    fallbackLng: "en",
    supportedLngs: SUPPORTED_LOCALES as unknown as string[],
    ns: NAMESPACES as unknown as string[],
    defaultNS: "common",
    resources: {
      en: englishResources,
    },
    partialBundledLanguages: true,
    backend: {
      loadPath: "/locales/{{lng}}/{{ns}}.json",
    },
    interpolation: {
      escapeValue: false,
    },
    returnNull: false,
    returnEmptyString: false,
    react: {
      useSuspense: false,
    },
  });

export const changeLocale = (locale: SupportedLocale): Promise<unknown> => {
  try {
    localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    // Ignore localStorage failures (privacy mode, quota)
  }
  return i18n.changeLanguage(locale);
};

export default i18n;
